import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
  medicalCodeOptions,
  medicalDetailInputList,
  relationOptions,
} from '../../../../common/data';

import Button from '../../../../components/common/Button';
import Typography from '../../../../components/common/Typography';

import { MedicalExpensesStyled } from '../../../../asset/styled/deductions/MedicalExpensesStyled';

import CompensationSection from './CompensationSection';
import MedicalDetailItem from './MedicalDetailItem';
import DetailButtonSection from '../../../../components/DetailButtonSection';
import SlideRightDialog from '../../../../components/common/SlideRightDialog';
import { getMedicalDetail, updateMedicalInfo } from '../medicalSlice';
import {
  getAlertMessageFunction,
  getDeleteDialogConfig,
  handleNumberChange2,
  magamCertifyCheck,
} from '../../../../common/Util';
import { isValidate } from '../util';
import { setIsDataModified } from '../../../common/commonSlice';

const MedicalDetail = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const { selectedMedicalDetail, medicalDetail, initMedicalDetail, detailReload } = useSelector(
    state => state.medical,
  );
  const { isMagam, isCertify } = useSelector(state => state.common);
  const { t } = useTranslation('deductions/medicalExpenses');
  const { t: tCommon } = useTranslation('common');
  const { t: tDependents } = useTranslation('dependents');
  const relationList = relationOptions(tDependents);
  const inputList = medicalDetailInputList(t);
  const medicalCodeList = medicalCodeOptions(t);

  const [dataList, setDataList] = useState([]);

  useEffect(() => {
    if (selectedMedicalDetail?.htm001 && detailReload == -1) {
      dispatch(getMedicalDetail(selectedMedicalDetail));
    } else {
      onClose();
    }
  }, [selectedMedicalDetail]);

  useEffect(() => {
    if (detailReload != -1) {
      dispatch(getMedicalDetail(selectedMedicalDetail));
    }
  }, [detailReload]);

  useEffect(() => {
    // 초기화 후 필터링된 데이터만 설정
    const filteredData = medicalDetail.filter(item => item.htmPDFSeq !== '');
    setDataList(filteredData);
  }, [medicalDetail]);

  const codeList = disabled => {
    let _codeList = [...medicalCodeList];
    if (!disabled) {
      _codeList = _codeList.filter(
        item => item.value != '1' && item.value != '6' && item.value != '9',
      );
    }

    return _codeList.map((item, index) => {
      return (
        <option key={index} value={item.value}>
          {item.label}
        </option>
      );
    });
  };

  const handleAddButton = () => {
    if (!magamCertifyCheck(isMagam, isCertify)) {
      return;
    }

    setDataList(prev => {
      const newData = [...prev];
      newData.push({
        ...initMedicalDetail,
        htmPerRegNum: medicalDetail[0]?.htmPerRegNum,
        htmSilB: dataList[0]?.htmSilB || 0,
        htmSilB2: dataList[0]?.htmSilB2 || 0,
      });
      return newData;
    });
  };

  const handleDeleteButton = dataIndex => {
    if (!magamCertifyCheck(isMagam, isCertify)) {
      return;
    }

    let apiDelete = false;
    dataList[dataIndex].htmSeq != '' ? (apiDelete = true) : (apiDelete = false);
    if (apiDelete) {
      let formData = [];
      formData.push({
        htmSeq: dataList[dataIndex].htmSeq,
        htmPDFSeq: dataList[dataIndex].htmPDFSeq,
        htmPerRegNum: dataList[dataIndex].htmPerRegNum,
      });
      const deleteConfig = getDeleteDialogConfig(tCommon, 'medical', { formData });

      dispatch(deleteConfig);
    } else {
      setDataList(prev => {
        const newData = [...prev];
        newData.splice(dataIndex, 1);
        return newData;
      });
    }
  };

  const handleCompensationChange = e => {
    const { name, value: inputValue } = e.target;
    let value = inputValue?.trim();
    value = value?.replace(/,/g, '');

    dispatch(setIsDataModified(true));
    setDataList(prevDataList => {
      const updatedDataList = [...prevDataList];
      updatedDataList[0] = {
        ...updatedDataList[0],
        [name]: value,
      };
      return updatedDataList;
    });
  };

  const handleChange = (e, item, dataIndex) => {
    const { name, value: inputValue } = e.target;
    let value = inputValue?.trim();

    if (item.isNumber) {
      value = handleNumberChange2(value);
    }

    dispatch(setIsDataModified(true));
    setDataList(prevDataList => {
      const updatedDataList = [...prevDataList];
      updatedDataList[dataIndex] = {
        ...updatedDataList[dataIndex],
        [name]: value,
      };
      return updatedDataList;
    });
  };

  const handleSave = async () => {
    if (!magamCertifyCheck(isMagam, isCertify)) {
      return;
    }
    const alertMessage = getAlertMessageFunction();
    if (!isValidate(dataList, t)) {
      return;
    }
    let formData = dataList;
    try {
      const resultAction = await dispatch(updateMedicalInfo({ formData })).unwrap();

      if (resultAction.resultCode === 'OK') {
        alertMessage(tCommon('alertMessage.saveSuccess'), 'success');
        onClose();
      } else {
        alertMessage(tCommon('alertMessage.saveFail'), 'error');
      }
    } catch (error) {
      alertMessage(tCommon('alertMessage.saveFail'), 'error');
      console.error('저장 중 오류 발생: ', error);
    }
  };

  return (
    <SlideRightDialog isOpen={isOpen} onClose={onClose}>
      <MedicalExpensesStyled>
        <div className="displayFlexColumn" style={{ gap: '24px' }}>
          <div className="displayFlexColumn" style={{ gap: '8px' }}>
            <Typography variant="body2Bold" color="Primary-500">
              {t('detail.title1')}
            </Typography>
            <Typography variant="subtitleBold">
              {`${medicalDetail[0]?.htmPerNm || ''} (${
                relationList.find(option => option.value === medicalDetail[0]?.htmPerType)?.label ||
                ''
              })`}
            </Typography>
          </div>
          {/* 의료비 상세 */}
          {dataList?.map((data, dataIndex) => {
            let htm006 = data.htm006 || '';
            let disabled =
              data.htmType == '1' &&
              data.htmPDFFlag == '2' &&
              (htm006 == '1' || htm006 == '6' || htm006 == '9');
            return (
              <MedicalDetailItem
                key={dataIndex}
                inputList={inputList}
                data={data}
                handleChange={handleChange}
                dataIndex={dataIndex}
                codeList={codeList}
                t={t}
                disabled={disabled}
                handleDeleteButton={handleDeleteButton}
              />
            );
          })}
          {/* 실비보상액 */}
          {dataList?.length > 0 && (
            <CompensationSection
              dataList={dataList || []}
              handleChange={handleCompensationChange}
            />
          )}
          {/* 의료비 추가 */}
          <Button
            value={t('detail.addButton')}
            style={{ width: '100%' }}
            addButton
            color="sub"
            onClick={handleAddButton}
          />
          {/* 취소,저장버튼 */}
          <DetailButtonSection onSave={handleSave} onClose={onClose} />
        </div>
      </MedicalExpensesStyled>
    </SlideRightDialog>
  );
};

export default MedicalDetail;
